import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, FileDown, Copy, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableColumnVisibilityMenu } from '@/components/shared/TableColumnVisibilityMenu'
import { ListLoader } from '@/components/shared/ListLoader'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'
import { useAuthStore } from '@/store/auth.store'
import { itemRequestApiService } from '@/services/itemRequestApiService'
import { purchaseRequisitionService } from '@/services/purchaseRequisitionService'
import { employeesService } from '@/services/employeesService'
import type { ItemRequestApiRow } from '@/api/types'

type ColumnKey = 'item' | 'quantity' | 'department' | 'requiredDate' | 'status' | 'action'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'item', labelKey: 'itemRequestPage.colItem' },
  { key: 'quantity', labelKey: 'itemRequestPage.colQuantity' },
  { key: 'department', labelKey: 'itemRequestPage.colDepartment' },
  { key: 'requiredDate', labelKey: 'itemRequestPage.colRequiredDate' },
  { key: 'status', labelKey: 'itemRequestPage.colStatus' },
  { key: 'action', labelKey: 'itemRequestPage.colAction' },
]

function formatDate(iso: string, locale: string) {
  if (!iso) return '—'
  const v = String(iso)
  const d = new Date(v.includes('T') ? v : `${v}T12:00:00`)
  if (Number.isNaN(d.getTime())) return v.slice(0, 10)
  return d.toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function csvEscape(cell: string | number) {
  return `"${String(cell).replace(/"/g, '""')}"`
}

export const ItemRequestList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en'
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<ItemRequestApiRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  /** API expects numeric status query; 0 = All */
  const [statusFilter, setStatusFilter] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [itemNameById, setItemNameById] = useState<Record<number, string>>({})
  const [departmentNameById, setDepartmentNameById] = useState<Record<number, string>>({})

  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    item: true,
    quantity: true,
    department: true,
    requiredDate: true,
    status: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId) {
      setItemNameById({})
      setDepartmentNameById({})
      return
    }

    let cancelled = false
    void Promise.allSettled([purchaseRequisitionService.getItems(branchId), employeesService.getDepartmentLookups()]).then(
      (results) => {
        if (cancelled) return

        const itemsRes = results[0]
        if (itemsRes.status === 'fulfilled') {
          const map: Record<number, string> = {}
          for (const it of itemsRes.value || []) {
            if (it?.id) map[it.id] = String(it.name ?? '').trim() || String(it.id)
          }
          setItemNameById(map)
        } else {
          setItemNameById({})
        }

        const deptsRes = results[1]
        if (deptsRes.status === 'fulfilled') {
          const map: Record<number, string> = {}
          for (const d of deptsRes.value || []) {
            const id = Number((d as any)?.id ?? 0)
            if (!id) continue
            const name = String((d as any)?.lookupNameEn ?? (d as any)?.lookupNameAr ?? '').trim()
            if (!name) continue
            map[id] = name
          }
          setDepartmentNameById(map)
        } else {
          setDepartmentNameById({})
        }
      },
    )

    return () => {
      cancelled = true
    }
  }, [branchId])

  const load = async () => {
    if (!branchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    setLoading(true)
    try {
      const status = statusFilter === 0 ? undefined : statusFilter
      const res = await itemRequestApiService.getAll(branchId, pageNumber, pageSize, status)
      const list = res.data || []
      const q = searchQuery.trim().toLowerCase()
      const filtered = q
        ? list.filter((r) =>
            [
              String(r.id ?? ''),
              String(
                itemNameById[Number(r.itemId ?? 0)] ??
                  r.itemName ??
                  r.itemNameEn ??
                  r.itemNameAr ??
                  r.itemId ??
                  '',
              ),
              String(departmentNameById[Number(r.departmentId ?? 0)] ?? r.departmentId ?? ''),
              String(r.quantity ?? ''),
              String(r.details ?? ''),
              String(r.requestedDate ?? ''),
              String(r.status ?? ''),
              String(r.approvalBy ?? ''),
            ].some((v) => String(v).toLowerCase().includes(q)),
          )
        : list
      setRows(filtered)
      setTotalCount(res.totalRecords || 0)
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? t('itemRequestPage.fetchFailed')
      const isNoData = /not found|no data|no records|no\s+.*\s+found/i.test(String(msg))
      setRows([])
      setTotalCount(0)
      if (!isNoData) toast.error(String(msg))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [branchId, searchQuery, statusFilter, pageNumber, pageSize, itemNameById, departmentNameById])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const page = Math.min(pageNumber, totalPages)
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const to = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount)

  const columnMenuItems = useMemo(
    () => ALL_COLUMNS.filter((c) => c.key !== 'action').map((c) => ({ id: c.key, label: t(c.labelKey) })),
    [t],
  )

  const toggleColumn = (id: string) => {
    const key = id as ColumnKey
    if (!ALL_COLUMNS.some((c) => c.key === key)) return
    if (key === 'action') return
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const statusBadge = (row: ItemRequestApiRow) => {
    const s = Number(row.status ?? 0)
    const text =
      s === 434
        ? t('itemRequestPage.statusPending')
        : s === 435
          ? t('itemRequestPage.statusApproved')
          : s === 436
            ? t('itemRequestPage.statusRejected')
            : String(row.status ?? '—')
    return (
      <Badge variant={s === 435 ? 'default' : s === 434 ? 'secondary' : 'outline'} className="text-xs">
        {text}
      </Badge>
    )
  }

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm(undefined, t('itemRequestPage.deleteConfirm'))
    if (!confirmed) return
    try {
      await itemRequestApiService.delete(id)
      await showSuccess(t('itemRequestPage.deletedTitle'), t('itemRequestPage.deletedMessage'))
      await load()
    } catch (e: any) {
      await showError(t('itemRequestPage.deleteFailedTitle'), e?.message ?? t('itemRequestPage.deleteFailedMessage'))
    }
  }

  const handleCopy = async () => {
    if (totalCount === 0) {
      toast.error(t('itemRequestPage.nothingToCopy'))
      return
    }
    if (!branchId) return
    const status = statusFilter === 0 ? undefined : statusFilter
    const res = await itemRequestApiService.getAll(branchId, 1, 10000, status)
    const header = ALL_COLUMNS.filter((c) => c.key !== 'action' && visibleColumns[c.key]).map((c) => t(c.labelKey))
    const lines = (res.data || []).map((r) => {
      const cells: (string | number)[] = []
      if (visibleColumns.item) cells.push(itemNameById[Number(r.itemId ?? 0)] ?? r.itemId ?? '—')
      if (visibleColumns.quantity) cells.push(r.quantity ?? '—')
      if (visibleColumns.department) cells.push(departmentNameById[Number(r.departmentId ?? 0)] ?? r.departmentId ?? '—')
      if (visibleColumns.requiredDate) cells.push(String(r.requestedDate ?? '').slice(0, 10))
      if (visibleColumns.status) cells.push(r.status ?? '—')
      return cells.join('\t')
    })
    await navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('itemRequestPage.copied'))
  }

  const handleExportCSV = () => {
    if (totalCount === 0) {
      toast.error(t('itemRequestPage.nothingToExport'))
      return
    }
    if (!branchId) return
    // Note: export uses current filter but fetches all pages in one call (best-effort).
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    {
      const status = statusFilter === 0 ? undefined : statusFilter
      itemRequestApiService.getAll(branchId, 1, 10000, status).then((res) => {
      const header = ALL_COLUMNS.filter((c) => c.key !== 'action' && visibleColumns[c.key])
        .map((c) => csvEscape(t(c.labelKey)))
        .join(',')
      const body = (res.data || [])
        .map((r) => {
          const cells: (string | number)[] = []
          if (visibleColumns.item) cells.push(itemNameById[Number(r.itemId ?? 0)] ?? r.itemId ?? '—')
          if (visibleColumns.quantity) cells.push(r.quantity ?? '—')
          if (visibleColumns.department) cells.push(departmentNameById[Number(r.departmentId ?? 0)] ?? r.departmentId ?? '—')
          if (visibleColumns.requiredDate) cells.push(String(r.requestedDate ?? '').slice(0, 10))
          if (visibleColumns.status) cells.push(r.status ?? '—')
          return cells.map(csvEscape).join(',')
        })
        .join('\n')
      const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `item_requests_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success(t('itemRequestPage.exported'))
      })
    }
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('itemRequestPage.pageTitle')}
        description={t('itemRequestPage.pageDescription')}
        action={{
          label: t('itemRequestPage.addRequest'),
          onClick: () => navigate('/self-services/item-requests/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('itemRequestPage.labelStatus')}</label>
                <Select
                  value={String(statusFilter)}
                  onValueChange={(v) => {
                    setStatusFilter(Number(v) || 0)
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[240px]">
                    <SelectValue placeholder={t('itemRequestPage.placeholderAll')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('itemRequestPage.placeholderAll')}</SelectItem>
                    <SelectItem value="434">{t('itemRequestPage.statusPending')}</SelectItem>
                    <SelectItem value="435">{t('itemRequestPage.statusApproved')}</SelectItem>
                    <SelectItem value="436">{t('itemRequestPage.statusRejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('itemRequestPage.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-9"
                />
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <TableColumnVisibilityMenu
                  columns={columnMenuItems}
                  visibility={visibleColumns}
                  onToggle={toggleColumn}
                  triggerLabel={t('itemRequestPage.columnVisibility')}
                  menuTitle={t('itemRequestPage.columnsToggle')}
                />
                <Button variant="outline" size="sm" onClick={handleExportCSV}>
                  <FileDown className="mr-2 h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => void handleCopy()}>
                  <Copy className="mr-2 h-4 w-4" />
                  {t('itemRequestPage.copy')}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  {t('itemRequestPage.print')}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      {visibleColumns.item && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('itemRequestPage.colItem')}
                        </TableHead>
                      )}
                      {visibleColumns.quantity && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('itemRequestPage.colQuantity')}
                        </TableHead>
                      )}
                      {visibleColumns.department && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('itemRequestPage.colDepartment')}
                        </TableHead>
                      )}
                      {visibleColumns.requiredDate && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('itemRequestPage.colRequiredDate')}
                        </TableHead>
                      )}
                      {visibleColumns.status && (
                        <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                          {t('itemRequestPage.colStatus')}
                        </TableHead>
                      )}
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary w-28">
                        {t('itemRequestPage.colAction')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                          {t('itemRequestPage.noData')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow
                          key={row.id ?? Math.random()}
                          className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                        >
                          {visibleColumns.item && (
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {itemNameById[Number(row.itemId ?? 0)] ??
                                row.itemName ??
                                row.itemNameEn ??
                                row.itemNameAr ??
                                (row.itemId != null ? String(row.itemId) : '—')}
                            </TableCell>
                          )}
                          {visibleColumns.quantity && (
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {row.quantity ?? '—'}
                            </TableCell>
                          )}
                          {visibleColumns.department && (
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {departmentNameById[Number(row.departmentId ?? 0)] ??
                                (row.departmentId != null ? String(row.departmentId) : '—')}
                            </TableCell>
                          )}
                          {visibleColumns.requiredDate && (
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {formatDate(String(row.requestedDate ?? '').slice(0, 10), locale)}
                            </TableCell>
                          )}
                          {visibleColumns.status && (
                            <TableCell className="px-4 py-2 text-sm text-center align-middle">
                              {statusBadge(row)}
                            </TableCell>
                          )}
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-primary hover:bg-primary/10"
                                onClick={() => navigate(`/self-services/item-requests/${row.id}`)}
                                title={t('itemRequestPage.editTitle')}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                onClick={() => void handleDelete(Number(row.id))}
                                title={t('itemRequestPage.deleteTitle')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('itemRequestPage.pageSize')}</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v))
                      setPageNumber(1)
                    }}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="text-sm text-muted-foreground">
                  {t('itemRequestPage.paginationShowing', { from, to, total: totalCount })}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('itemRequestPage.previous')}
                  </Button>
                  <span className="text-sm">{t('itemRequestPage.pageOf', { page, totalPages })}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    {t('itemRequestPage.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

